import { ApiError, type Role, type User } from '@vlm/api';
import { toast } from '@vlm/ui';
import { create } from 'zustand';
import { translate } from '../i18n';
import { api, SESSION_KEY, sessionEvents, sessionStore } from './api';
import { errorText, isRefusal } from './errors';
import { usePrefs } from './prefs';
import { queryClient } from './query';

/** checking — a stored session is being confirmed · unreachable — the API did not answer (the session is kept). */
export type AuthStatus = 'checking' | 'signedIn' | 'signedOut' | 'unreachable';

interface AuthState {
  status: AuthStatus;
  user: User | null;
  /** The user signed out themselves: the sign-in page starts clean, not on their last page (it may be another company's next). */
  left: boolean;
}

export const useAuth = create<AuthState>(() => ({
  status: sessionStore.get() ? 'checking' : 'signedOut',
  user: null,
  left: false,
}));

/** The user's saved language wins over this browser's (as in the old admin). */
function adopt(user: User) {
  if (user.language === 'en' || user.language === 'ar') usePrefs.getState().setLang(user.language);
}

function clear(left = false) {
  queryClient.clear();
  useAuth.setState({ status: 'signedOut', user: null, left });
}

/**
 * Why the refresh behind a stored session was refused while it was being confirmed ("Your company
 * trial has ended") — more telling than the request's own 401 ("Company account is not active").
 */
let refusedWhileChecking: ApiError | null = null;

/** On load: confirm the stored session by reading the profile. */
export async function restoreSession(): Promise<void> {
  if (!sessionStore.get()) {
    useAuth.setState({ status: 'signedOut', user: null });
    return;
  }
  useAuth.setState({ status: 'checking' });
  refusedWhileChecking = null;
  try {
    const user = await api.profile.get();
    adopt(user);
    useAuth.setState({ status: 'signedIn', user });
  } catch (error) {
    // refused (a dead session, a stopped account): sign out; away or failing (a restart, a 502): keep it
    if (error instanceof ApiError && error.status < 500) {
      sessionStore.set(null);
      clear();
      // the account or its company was stopped since the last visit: say so on the sign-in page
      const why = refusedWhileChecking && isRefusal(refusedWhileChecking) ? refusedWhileChecking : error;
      if (isRefusal(why)) toast.error(errorText(why, translate));
    } else {
      useAuth.setState({ status: 'unreachable' });
    }
  }
}

export async function signIn(username: string, password: string): Promise<User> {
  const login = await api.auth.login(username, password);
  sessionStore.set({ accessToken: login.accessToken, refreshToken: login.refreshToken });
  queryClient.clear();
  adopt(login.user);
  useAuth.setState({ status: 'signedIn', user: login.user, left: false });
  return login.user;
}

/** The API has no sign-out endpoint (tokens simply expire); forgetting them is the sign-out. */
export function signOut() {
  sessionStore.set(null);
  clear(true);
}

/** Keep the signed-in user fresh after a profile change. */
export function setUser(user: User) {
  useAuth.setState({ user });
}

sessionEvents.expired = (reason) => {
  const status = useAuth.getState().status;
  const wasSignedIn = status === 'signedIn';
  // a stored session being confirmed: restoreSession tells the user, with this reason
  if (status === 'checking') refusedWhileChecking = reason;
  clear();
  if (!wasSignedIn) return;
  // a suspended company or a deactivated account is not an expired session: tell the user which
  if (reason && isRefusal(reason)) toast.error(errorText(reason, translate));
  else toast.info(translate('auth.expired'));
};

sessionEvents.refreshed = (login) => {
  if (useAuth.getState().status === 'signedIn') useAuth.setState({ user: login.user });
};

// Another tab signed out (or in): follow it, so no tab keeps acting on a session that is gone.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== SESSION_KEY) return;
    const status = useAuth.getState().status;
    if (!event.newValue && status !== 'signedOut') clear();
    else if (event.newValue && status === 'signedOut') void restoreSession();
  });
}

export const MANAGERS: readonly Role[] = ['ADMIN', 'OPERATOR'];
export const ADMINS: readonly Role[] = ['ADMIN'];

export const useUser = () => useAuth((s) => s.user);

/** Whether the signed-in user's role is one of `roles` (the API enforces the same rules). */
export function useCan(roles: readonly Role[]): boolean {
  const role = useAuth((s) => s.user?.role);
  return role !== undefined && roles.includes(role);
}
